import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DeadStockTag } from "./DeadStockTag";
import { TablePagination } from "../../leave-management/components/TablePagination";

export function StockPositionTable({
  data,
  page,
  pageSize,
  onPageChange,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Paginated list
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize]);

  // Format currency helper
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const handleRowClick = (item) => {
    console.log("Stock Position details clicked:", item);
    navigate(`/inventory-management/stock-position/${item.productId}`);
  };

  return (
    <div className="space-y-4 font-sans">
      <div className="overflow-x-auto rounded-xl border border-[var(--color-text-border)] bg-white shadow-sm">
        <table className="w-full text-sm table-fixed min-w-[900px] border-collapse">
          <colgroup>
            <col className="w-[20%]" />
            <col className="w-[20%]" />
            <col className="w-[9%]" />
            <col className="w-[9%]" />
            <col className="w-[9%]" />
            <col className="w-[9%]" />
            <col className="w-[9%] bg-[#EBF2FC]" /> {/* Highlight column */}
            <col className="w-[13%]" />
            <col className="w-[12%]" />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30 border-b border-[var(--color-text-border)]/60">
              <th className="px-4 py-3.5 font-bold text-left">{t("inventory.product")}</th>
              <th className="px-4 py-3.5 font-bold text-left">{t("inventory.warehouse")}</th>
              <th className="px-4 py-3.5 font-bold text-right">{t("inventory.total")}</th>
              <th className="px-4 py-3.5 font-bold text-right">{t("status.reserved")}</th>
              <th className="px-4 py-3.5 font-bold text-right">{t("inventory.maintReserve")}</th>
              <th className="px-4 py-3.5 font-bold text-right">{t("inventory.defective")}</th>
              <th className="px-4 py-3.5 font-bold text-right bg-[#EBF2FC] text-[var(--color-primary)]">
                {t("status.available")}
              </th>
              <th className="px-4 py-3.5 font-bold text-right">{t("inventory.stockValue")}</th>
              <th className="px-4 py-3.5 font-bold text-center">{t("inventory.deadStock")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-text-border)]/35">
            {pagedRows.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row)}
                className="hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer"
              >
                {/* Product with Part Number */}
                <td className="px-4 py-3">
                  <span className="font-bold text-foreground block truncate leading-tight">
                    {row.productName}
                  </span>
                  <span className="text-[11px] font-semibold text-[var(--color-text-secondary)] block mt-0.5 font-mono">
                    {row.partNumber}
                  </span>
                </td>

                {/* Warehouse */}
                <td className="px-4 py-3 text-[var(--color-text-secondary)] font-medium truncate">
                  {row.warehouse}
                </td>

                {/* Numeric Fields */}
                <td className="px-4 py-3 text-right font-medium text-foreground">
                  {row.totalStock.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right font-medium text-[var(--color-text-secondary)]">
                  {row.reservedStock > 0 ? row.reservedStock.toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-right font-medium text-[var(--color-text-secondary)]">
                  {row.maintenanceReserve > 0 ? row.maintenanceReserve.toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-right font-medium text-rose-600">
                  {row.defectiveStock > 0 ? row.defectiveStock.toLocaleString() : "—"}
                </td>

                {/* Available Stock (Visually Shaded + Bold Primary) */}
                <td className="px-4 py-3 text-right font-extrabold text-[var(--color-primary)] bg-[#EBF2FC]">
                  {row.availableStock.toLocaleString()}
                </td>

                {/* Stock Value */}
                <td className="px-4 py-3 text-right font-bold text-foreground">
                  {formatCurrency(row.stockValue)}
                </td>

                {/* Dead Stock Tag */}
                <td className="px-4 py-3 text-center">
                  <DeadStockTag isDead={row.isDeadStock} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Wrapper */}
      <TablePagination
        page={page}
        totalPages={totalPages}
        totalItems={data.length}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </div>
  );
}
